import React, { useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import Modal from "react-modal";
import StyledModal from "../Modal/StyledModal";
import LocalStorage from "../../../shared/localStorage";
import Axios from "../../../services/Axios";
import APIVariables from "../../../services/api-variables";
import { Auth } from "../../../routes/Constants";
import { ReactComponent as Warning } from "../../../assets/icons/warning.svg";

const isLoggedIn = () => {
  try {
    const getLocalStorage = localStorage.getItem(LocalStorage.USER_NHSPORTAL);
    const parseLocalStorage = JSON.parse(getLocalStorage);
    return Boolean(parseLocalStorage?.isLoggedIn);
  } catch {
    return false;
  }
};

const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes (milliseconds)
const COUNTDOWN_TIME = 10 * 60; // 10 minutes (seconds)

const SessionWarning = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [expired, setExpired] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_TIME);
  const intervalRef = useRef();

  const shouldShow = isLoggedIn();

  const signOut = async () => {
    Axios.get(APIVariables.USERS_LOGOUT)
      .then(() => {
        localStorage.removeItem(LocalStorage.USER_NHSPORTAL);
        window.location.href = Auth.LOGIN;
      })
      .catch(() => {
        localStorage.removeItem(LocalStorage.USER_NHSPORTAL);
        window.location.href = Auth.LOGIN;
      });
  };

  const idleTimer = useIdleTimer({
    timeout: IDLE_TIMEOUT,
    onIdle: () => {
      if (shouldShow) {
        setShowWarning(true);
        setExpired(false);
        setCountdown(COUNTDOWN_TIME);
      }
    },
    // don't auto-hide the warning when the modal is visible — only hide on activity when not showing
    onActive: () => {
      if (!expired && !showWarning) setShowWarning(false);
    },
    events: ["keydown", "mousedown", "touchstart"],
    debounce: 500,
    disabled: !shouldShow,
  });

  // countdown logic
  useEffect(() => {
    if (showWarning && !expired) {
      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            setExpired(true);
            setShowWarning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [showWarning, expired]);

  useEffect(() => {
    if (expired) {
      // clear session and redirect to login
      try {
        localStorage.removeItem(LocalStorage.USER_NHSPORTAL);
      } catch {}
      window.location.href = "/login";
    }
  }, [expired]);

  if (!shouldShow) return null;

  return (
    <>
      {(showWarning || expired) && (
        <StyledModal>
          <Modal
            isOpen={showWarning || expired}
            onRequestClose={() => setShowWarning(false)}
            style={{
              content: {
                top: "50%",
                left: "50%",
                right: "auto",
                bottom: "auto",
                marginRight: "-50%",
                transform: "translate(-50%, -50%)",
                width: "520px",
                borderRadius: "12px",
              },
            }}
            contentLabel="Session timeout"
            overlayClassName="overlay-modal"
            shouldCloseOnOverlayClick={false}
          >
            <div className="wrapper-modal">
              <div className="image">
                <Warning />
              </div>

              <div className="second-text" >
                {!expired ? (
                  <div className="col2 flex-center">
                     <div style={{ fontWeight: 600, fontSize: 28, textAlign: "center", padding: '20px 40px', backgroundColor: '#f5f5f5', borderRadius: 8, marginBottom: 12 }}>
                      {String(Math.floor(countdown / 60)).padStart(2, "0")}:
                      {String(countdown % 60).padStart(2, "0")}
                    </div>
                    Your account has remained inactive for 5 minutes. Please select 'Active' or you will be automatically signed out.
                  </div>
                ) : (
                  <>Your session has expired. You will be redirected to the login page.</>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 16 }}>
                <button
                  style={{width: '140px' }}
                  className='red-button'
                  onClick={signOut}
                >
                  {expired ? "Go to login" : "Sign out"}
                </button>
                {!expired && (
                  <button
                    className="primary-button"
                    onClick={() => {
                      idleTimer.reset();
                      setShowWarning(false);
                    }}
                  >
                    Active
                  </button>
                )}
              </div>
            </div>
          </Modal>
        </StyledModal>
      )}
    </>
  );
};

export default SessionWarning;